import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { compose } from "redux";

import moment from "moment";
import numeral from "numeral";
import { toast } from "react-toastify";

import * as loginReducer from "containers/Login/reducer";
import * as accountsReducer from "containers/Accounts/reducer";

import * as adminReducer from "containers/Admin/reducer";
import * as analyticsReducer from "containers/Analytics/reducer";
import * as foldersReducer from "containers/Folders/reducer";
import * as globalReducer from "containers/App/reducer";

import { formatBytes } from "containers/Upload/reducer";

import databaseImg from "images/admin_images/database-light.svg";
import filesImg from "images/admin_images/files-light 1.svg";
import messageImg from "images/admin_images/message-lines-light 1.svg";
import sparklesImg from "images/admin_images/sparkles-light 1.svg";
import usersImg from "images/admin_images/users-light 1.svg";

import LoadingIndicator from "components/LoadingIndicator/LoadingIndicator";

import { useDispatch, useSelector } from "types/store";

import { Button, Icon } from "aible";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfo } from "@fortawesome/free-solid-svg-icons";

import styles from "./style.module.css";

const AnalyticsView = ({} = {}) => {
  const dispatch = useDispatch();

  const isLoadingUsers = useSelector((state) => adminReducer.getIsLoadingUsers(state));
  const users = useSelector((state) => adminReducer.getUsers(state));

  const hasAnalytics = useSelector((state) => analyticsReducer.getHasAnalytics(state));
  const analyticsData = useSelector((state) => analyticsReducer.getAnalyticsData(state));
  const isAnalyticsLoading = useSelector((state) => globalReducer.getIsAnalyticsLoading(state));

  const analyticsRefreshDate = useSelector((state) => analyticsReducer.getAnalyticsRefreshDate(state));

  const handleRefresh = () => {
    return dispatch(analyticsReducer.fetchAnalytics(true));
  };

  useEffect(() => {
    if (!isAnalyticsLoading && !hasAnalytics) {
      dispatch(analyticsReducer.fetchAnalytics());
    }
  }, []);

  const activeUsers = users.filter((user) => user?.status !== "DELETED" && user?.type !== "PENDING_USER");

  if (isAnalyticsLoading && !hasAnalytics) {
    return (
      <div className={styles.analyticsPanel}>
        <div className={styles.analyticsLoading}>
          <LoadingIndicator
            style={{
              width: "20px",
              height: "18px",
              // transform: "scale(0.40)",
              margin: "-10px 0px 3px 0px"
            }}
          />
        </div>
      </div>
    );
  }

  const userCount = activeUsers?.length ?? 0;

  const chatCount = analyticsData?.chat_count ?? 0;

  const docSetCount = analyticsData?.document_set_count ?? 0;
  const docCount = analyticsData?.document_count ?? 0;
  const docSize = formatBytes(analyticsData?.total_document_size ?? 0);

  const dataCount = analyticsData?.dataset_count ?? 0;
  const dataRows = analyticsData?.dataset_row_count ?? 0;
  const dataCombs = analyticsData?.combination_count ?? 0;

  const modelCount = analyticsData?.model_count ?? 0;

  const analyticsDate = moment.utc(analyticsRefreshDate).local().format("MMM D h:mm:ss A");

  return (
    <React.Fragment>
      <div className={styles.analyticsPanel}>
        <div className={styles.analyticsRow}>
          <div className={styles.analyticsCard}>
            <div className={styles.acActivityHeader}>{`TOTAL ACTIVITY AS OF:`}</div>
            <div className={styles.acActivityContent}>{analyticsDate}</div>
            <div className={styles.acActivityActions}>
              <Button type={"secondary"} onClick={handleRefresh} isLoading={isAnalyticsLoading}>
                {"Refresh Now"}
              </Button>
              <div className={styles.acActivityTooltip}>
                <div className={styles.acActivityTooltipIcon}>
                  <FontAwesomeIcon icon={faInfo} />
                </div>
                <div className={styles.acActivityTooltipContent}>
                  {"This may take several minutes to pull all of the data"}
                </div>
              </div>
            </div>
          </div>
          <div className={styles.analyticsCard}>
            <AnalyticsCardHeader icon={usersImg} title={"Total Members"} subtitle={"Across all folders"} />
            <AnalyticsCardItem value={userCount} isLoading={isLoadingUsers} />
          </div>
          {hasAnalytics && (
            <div className={styles.analyticsCard}>
              <AnalyticsCardHeader
                icon={messageImg}
                title={"Total Questions Submitted"}
                subtitle={"Across all agent templates"}
              />
              <AnalyticsCardItem value={chatCount} />
            </div>
          )}
        </div>
        {hasAnalytics && (
          <div className={styles.analyticsRow}>
            <div className={styles.analyticsCardLarge}>
              <AnalyticsCardHeader
                icon={filesImg}
                title={"Unstructured Data Activity"}
                subtitle={"Retrieval Augmented Generation"}
                style={{ borderBottom: "1px solid #DCDAEC" }}
              />
              <AnalyticsCardItem label={"Document Sets Created"} value={docSetCount} />
              <AnalyticsCardItem label={"Total Documents Uploaded"} value={docCount} />
              <AnalyticsCardItem label={"Total Size of Documents Uploaded"} value={docSize} />
            </div>
            <div className={styles.analyticsCardLarge}>
              <AnalyticsCardHeader
                icon={databaseImg}
                title={"Structured Data Activity"}
                subtitle={"Automated Analysis and NLQ"}
                style={{ borderBottom: "1px solid #DCDAEC" }}
              />
              <AnalyticsCardItem label={"Total Datasets Added"} value={dataCount} />
              <AnalyticsCardItem label={"Total Dataset Rows"} value={dataRows} />
              <AnalyticsCardItem label={"Variable Combinations Evaluated"} value={dataCombs} />
            </div>
            <div className={styles.analyticsCardLarge}>
              <AnalyticsCardHeader
                icon={sparklesImg}
                title={"Predictive Modeling Activity"}
                subtitle={"Modeling and Optimization of Structured Datasets"}
                style={{ borderBottom: "1px solid #DCDAEC" }}
              />
              <AnalyticsCardItem label={"Predictive Models Trained"} value={modelCount} />
            </div>
          </div>
        )}
      </div>
    </React.Fragment>
  );
};

const AnalyticsCardHeader = ({ icon, title, subtitle, className, ...retProps } = {}) => {
  return (
    <div className={styles.analyticsCardHeader + (className ? " " + className : "")} {...retProps}>
      {icon && (
        <div className={styles.analyticsCardHeaderIcon}>
          <Icon src={icon} size={48} />
        </div>
      )}
      <div className={styles.analyticsCardHeaderContent}>
        <div className={styles.analyticsCardHeaderTitle}>{title}</div>
        <div className={styles.analyticsCardHeaderSubtitle}>{subtitle}</div>
      </div>
    </div>
  );
};

const formatValue = (val) => {
  if (isNaN(val)) {
    return val;
  }
  if (Math.abs(val) >= 1000000) {
    return numeral(val).format("0.[00]a");
  }
  return numeral(val).format("0,0");
};

const AnalyticsCardItem = ({ label, value, isLoading, className, ...retProps } = {}) => {
  return (
    <div className={styles.analyticsCardItem + (className ? " " + className : "")} {...retProps}>
      {label ? <div className={styles.analyticsCardItemLabel}>{label}</div> : null}
      {isLoading ? (
        <div className={styles.analyticsCardItemValue}>
          <LoadingIndicator
            style={{
              width: "20px",
              height: "18px",
              transform: "scale(0.70)",
              margin: "-5px 0px 3px -20px"
            }}
          />
        </div>
      ) : value ? (
        <div className={styles.analyticsCardItemValue}>{formatValue(value)}</div>
      ) : null}
    </div>
  );
};

export default AnalyticsView;
